import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, Outlet } from 'react-router-dom';
import { horodatyApi } from '@/lib/utils/horodaty-api';

/**
 * Contexte pour l'authentification des invités (via OTP)
 * 
 * Après OTP login, le token Sanctum est stocké dans un cookie HTTP-only
 * (horodaty_guest_token) géré automatiquement par le navigateur.
 * 
 * Le frontend ne stocke plus le token en localStorage/sessionStorage.
 * L'état isAuthenticated est basé sur un state local qui passe à true après OTP success.
 */

type InvitationContext = {
    procedure_template: {
        name: string;
        uniq_id: string;
    };
    company: {
        name: string;
    };
    folder?: {
        name: string;
        uniq_id: string;
    };
    subfolder?: {
        name: string;
        uniq_id: string;
    };
    email: string;
    is_otp_required: boolean;
    expires_at: string;
    status: string;
};

type GuestUser = {
    uniq_id: string;
    email: string;
    first_name: string;
    last_name: string;
};

type GuestAuthContextType = {
    /** Indique si l'invité est authentifié (OTP validé) */
    isAuthenticated: boolean;
    /** Indique si la vérification de session est en cours (au mount) */
    isCheckingSession: boolean;
    /** Informations de l'utilisateur guest (après OTP) */
    guestUser: GuestUser | null;
    /** Contexte de l'invitation (procédure, entreprise, etc.) */
    invitationContext: InvitationContext | null;
    /** Indique si une opération est en cours */
    isLoading: boolean;
    /** Message d'erreur éventuel */
    error: string | null;
    /** UUID de l'invitation (depuis l'URL) */
    invitationUuid: string | null;
    /** Charge le contexte de l'invitation */
    loadInvitationContext: () => Promise<InvitationContext | null>;
    /** Demande l'envoi d'un code OTP */
    requestOtp: () => Promise<boolean>;
    /** Vérifie le code OTP et authentifie l'invité */
    verifyOtp: (code: string) => Promise<boolean>;
    /** Réinitialise l'erreur */
    clearError: () => void;
    /** Déconnecte l'invité */
    logout: () => Promise<void>;
    /** Marque l'invitation comme complétée (après finalisation du rapport) */
    markInvitationCompleted: () => void;
};

const GuestAuthContext = createContext<GuestAuthContextType | null>(null);

export function GuestAuthProvider({ children }: { children?: React.ReactNode }) {
    const { uuid } = useParams<{ uuid: string }>();
    console.log('[GuestAuth] Provider MOUNT, uuid:', uuid);
    
    // État d'authentification (basé sur le succès OTP, le cookie est géré par le navigateur)
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const [guestUser, setGuestUser] = useState<GuestUser | null>(null);
    const [invitationContext, setInvitationContext] = useState<InvitationContext | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Au mount, charger le contexte pour vérifier si un guest est déjà authentifié via cookie.
     * Cela débloque isCheckingSession pour toutes les pages enfants (landing, report, etc.)
     */
    useEffect(() => {
        if (!uuid) {
            setIsCheckingSession(false);
            return;
        }
        horodatyApi.getPublicInvitation(uuid)
            .then((response) => {
                const raw = response.data?.data ?? response.data;
                const context = {
                    ...raw,
                    status: typeof raw.status === 'object' ? raw.status.value : raw.status,
                };
                setInvitationContext(context);
                if (raw.authenticated_guest) {
                    setGuestUser(raw.authenticated_guest);
                    setIsAuthenticated(true);
                }
            })
            .catch(() => {
                // Pas grave, la landing page gèrera l'erreur
            })
            .finally(() => setIsCheckingSession(false));
    }, [uuid]);

    /**
     * Charge le contexte de l'invitation depuis l'API.
     * Si déjà chargé (par le useEffect du mount), retourne le cache.
     */
    const loadInvitationContext = useCallback(async (): Promise<InvitationContext | null> => {
        // Déjà chargé par le useEffect initial → pas de re-fetch
        if (invitationContext) {
            return invitationContext;
        }

        if (!uuid) {
            setError("UUID d'invitation manquant");
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await horodatyApi.getPublicInvitation(uuid);
            const raw = response.data?.data ?? response.data;
            console.log('[GuestAuth] API getPublicInvitation raw response:', JSON.stringify(raw.status));
            // Normaliser le status : l'API retourne {value, label}, on garde juste la valeur string
            const context = {
                ...raw,
                status: typeof raw.status === 'object' ? raw.status.value : raw.status,
            };
            console.log('[GuestAuth] Normalized status:', context.status);
            setInvitationContext(context);

            // Si le serveur a détecté un guest déjà authentifié via cookie
            if (raw.authenticated_guest) {
                setGuestUser(raw.authenticated_guest);
                setIsAuthenticated(true);
            }
            setIsCheckingSession(false);

            return context;
        } catch (err: any) {
            const message = err.response?.data?.message || "Impossible de charger l'invitation";
            setError(message);
            setIsCheckingSession(false);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [uuid, invitationContext]);

    /**
     * Demande l'envoi d'un code OTP par email
     */
    const requestOtp = useCallback(async (): Promise<boolean> => {
        if (!uuid) {
            setError("UUID d'invitation manquant");
            return false;
        }

        setIsLoading(true);
        setError(null);

        try {
            await horodatyApi.requestInvitationOtp(uuid);
            return true;
        } catch (err: any) {
            const message = err.response?.data?.message || "Impossible d'envoyer le code de vérification";
            setError(message);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [uuid]);

    /**
     * Vérifie le code OTP et authentifie l'invité
     * Le token Sanctum est stocké dans un cookie HTTP-only par l'API
     */
    const verifyOtp = useCallback(async (code: string): Promise<boolean> => {
        if (!uuid) {
            setError("UUID d'invitation manquant");
            return false;
        }

        if (!code || code.length !== 6) {
            setError("Le code doit contenir 6 chiffres");
            return false;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await horodatyApi.verifyInvitationOtp(uuid, code);
            console.log('[GuestAuth] OTP response:', response.data);
            
            // Le token est maintenant dans un cookie HTTP-only, pas dans le body
            // On vérifie juste si l'API a retourné un status success
            if (response.data?.status === 'authenticated') {
                // Stocker les infos utilisateur si disponibles
                if (response.data?.user) {
                    setGuestUser(response.data.user);
                }
                setIsAuthenticated(true);
                console.log('[GuestAuth] Authentication successful via cookie');
                return true;
            } else {
                setError("Authentification échouée");
                return false;
            }
        } catch (err: any) {
            const message = err.response?.data?.message || "Code de vérification invalide";
            setError(message);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [uuid]);

    /**
     * Réinitialise l'erreur
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Déconnecte l'invité (révoque le token et supprime le cookie)
     */
    const logout = useCallback(async () => {
        try {
            if (guestUser?.uniq_id) {
                await horodatyApi.logoutGuest(guestUser.uniq_id);
            }
        } catch (err) {
            console.error('[GuestAuth] Logout error:', err);
        }
        setIsAuthenticated(false);
        setGuestUser(null);
    }, [guestUser]);

    const markInvitationCompleted = useCallback(() => {
        console.log('[GuestAuth] markInvitationCompleted called');
        setInvitationContext((prev) => {
            console.log('[GuestAuth] Previous status:', prev?.status, '-> completed');
            return prev ? { ...prev, status: 'completed' as const } : prev;
        });
    }, []);

    const value = useMemo<GuestAuthContextType>(() => ({
        isAuthenticated,
        isCheckingSession,
        guestUser,
        invitationContext,
        isLoading,
        error,
        invitationUuid: uuid ?? null,
        loadInvitationContext,
        requestOtp,
        verifyOtp,
        clearError,
        logout,
        markInvitationCompleted,
    }), [
        isAuthenticated,
        isCheckingSession,
        guestUser,
        invitationContext,
        isLoading,
        error,
        uuid,
        loadInvitationContext,
        requestOtp,
        verifyOtp,
        clearError,
        logout,
        markInvitationCompleted,
    ]);

    return (
        <GuestAuthContext.Provider value={value}>
            {children ?? <Outlet />}
        </GuestAuthContext.Provider>
    );
}

/**
 * Hook pour accéder au contexte d'authentification guest
 */
export function useGuestAuth() {
    const context = useContext(GuestAuthContext);
    if (!context) {
        throw new Error('useGuestAuth must be used within a GuestAuthProvider');
    }
    return context;
}
